<template>
  <v-container :class="containerClass" fluid>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="itemList.data"
          :loading="isLoading"
          :options.sync="options"
          :server-items-length="itemList.meta.total"
          :footer-props="defaultDataTableFooterProps"
          item-key="id"
        >
          <template #top>
            <v-sheet>
              <v-container fluid class="pa-4">
                <v-row align="center">
                  <v-col md="6" cols="12">
                    <h2>Holiday List</h2>
                  </v-col>
                  <v-col md="6" cols="12" class="text-right">
                    <v-btn
                      v-if="hasPermission('holidays.create')"
                      color="primary"
                      class="mx-1"
                      :small="$vuetify.breakpoint.mobile"
                      :href="$route('holidays.create')"
                    >
                      <v-icon :small="$vuetify.breakpoint.mobile" left
                        >mdi-plus</v-icon
                      >Tambah
                    </v-btn>
                    <v-btn
                      class="mx-1"
                      :small="$vuetify.breakpoint.mobile"
                      @click="showFilters = !showFilters"
                    >
                      <v-icon :small="$vuetify.breakpoint.mobile" left
                        >mdi-filter</v-icon
                      >{{ showFilters ? 'Hide' : 'Show' }} Filter
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-sheet>
            <v-divider></v-divider>
            <v-expand-transition>
              <v-sheet v-show="showFilters" color="grey lighten-5">
                <v-container fluid class="pa-4">
                  <v-row>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">Classroom</div>
                      <v-autocomplete
                        v-model="filters.classroom_id"
                        :items="classrooms"
                        item-text="name"
                        item-value="id"
                        :loading="isClassroomListLoading"
                        :search-input.sync="searchInputClassroom"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Student"
                        hide-details
                        dense
                        clearable
                        cache-items
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">Status</div>
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Status"
                        hide-details
                        dense
                        clearable
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">Start Date</div>
                      <date-picker
                        v-model="filters.start_date"
                        value-type="format"
                        type="date"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">End Date</div>
                      <date-picker
                        v-model="filters.end_date"
                        value-type="format"
                        type="date"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">Created At</div>
                      <date-picker
                        v-model="filters.created_at"
                        value-type="format"
                        type="datetime"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="3" cols="12">
                      <div class="label mb-2">Updated At</div>
                      <date-picker
                        v-model="filters.updated_at"
                        value-type="format"
                        type="datetime"
                        range
                        class="mx-datepicker--dense"
                        confirm
                        @confirm="getListData"
                        @clear="getListData"
                      ></date-picker>
                    </v-col>
                    <v-col md="4" cols="12">
                      <v-row class="my-auto" align="center">
                        <v-col md="6" cols="12">
                          <v-switch
                            v-model="filters.is_global"
                            inset
                            hide-details
                            label="Libur Nasional?"
                          />
                        </v-col>
                        <v-col md="6" cols="12">
                          <v-switch
                            v-model="filters.is_annual"
                            inset
                            hide-details
                            label="Libur Tahunan?"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-container>
                <v-divider></v-divider>
              </v-sheet>
            </v-expand-transition>
          </template>
          <template #[`item.name`]="{ item }">
            <v-row dense align="center">
              <v-col cols="12" md="auto">
                <strong>{{ bilingualText(item, 'name', 'name_cn') }}</strong>
              </v-col>
              <v-col cols="12" md="auto">
                <v-chip-group>
                  <v-chip v-if="item.is_global" small color="info" dark>
                    <strong>Nasional</strong>
                  </v-chip>
                  <v-chip v-if="item.is_annual" small color="warning" dark>
                    <strong>Tahunan</strong>
                  </v-chip>
                </v-chip-group>
              </v-col>
            </v-row>
          </template>
          <template #[`item.start_date`]="{ item }">
            {{ formatDate(item.start_date, 'DD MMM YYYY') }}
          </template>
          <template #[`item.end_date`]="{ item }">
            {{ formatDate(item.end_date, 'DD MMM YYYY') }}
          </template>
          <template #[`item.classrooms`]="{ item }">
            <ul v-if="item.classrooms.length > 0">
              <li v-for="classroom in item.classrooms" :key="classroom.id">
                {{ classroom.name }}
              </li>
            </ul>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="holidayStatusProps(item.status, 'color')"
              small
              dark
            >
              <strong>
                {{ holidayStatusProps(item.status, 'text') }}
              </strong>
            </v-chip>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at, 'YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at, 'YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template #[`item.action`]="{ item }">
            <div class="text-right">
              <v-btn
                v-if="hasPermission('holidays.view')"
                small
                color="secondary"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                :href="$route('holidays.detail', item.id)"
              >
                <v-icon small>mdi-eye</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;View</span>
              </v-btn>
              <v-btn
                v-if="hasPermission('holidays.update')"
                small
                color="warning"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                :href="$route('holidays.update', item.id)"
              >
                <v-icon small>mdi-pencil</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;Edit</span>
              </v-btn>
              <v-btn
                v-if="hasPermission('holidays.delete')"
                small
                color="error"
                class="ma-1"
                :icon="$vuetify.breakpoint.mobile"
                @click="showDeleteDialog(item)"
              >
                <v-icon small>mdi-delete-forever</v-icon>
                <span v-if="!$vuetify.breakpoint.mobile">&nbsp;Delete</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <holiday-delete-dialog
      v-if="deleteDialog.show"
      v-model="deleteDialog.show"
      :invoice="deleteDialog.item"
      @delete="getListData"
    >
    </holiday-delete-dialog>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import HolidayDeleteDialog from '@components/Holiday/DeleteDialog'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import constant from '@/constant'
import { ListParams } from '@/models'
import utils from '@/utils'

export default {
  name: 'Holiday',
  components: {
    HolidayDeleteDialog,
  },
  layout: LayoutDashboard,
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      showFilters: true,
      filters: {
        name: null,
        classroom_id: null,
        is_global: null,
        is_annual: null,
        status: null,
        created_at: [],
        updated_at: [],
        start_date: [],
        end_date: [],
      },
      deleteDialog: {
        show: false,
        item: null,
      },
      searchInputClassroom: null,
      headers: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Start Date', value: 'start_date' },
        { text: 'End Date', value: 'end_date' },
        { text: 'Classrooms', value: 'classrooms', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Created At', value: 'created_at' },
        { text: 'Updated At', value: 'updated_at' },
        { text: 'Action', value: 'action', sortable: false, align: 'center' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      itemList: 'holiday/list',
      isLoading: 'holiday/isListLoading',
      classroomList: 'classroom/searchList',
      isClassroomListLoading: 'classroom/isSearchListLoading',
    }),
    classrooms() {
      return this.classroomList.data
    },
    statusOptions() {
      return constant.holidayStatusOptions
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getListData()
      },
    },
    searchInputClassroom(val) {
      if (val) {
        this.debouncedSearchClassroomList()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchClassroomList = _.debounce(
      this.searchClassroomList,
      500
    )
  },
  methods: {
    ...mapActions({
      getItemList: 'holiday/getList',
      getClassroomList: 'classroom/search',
    }),
    ...mapMutations({
      setListParams: 'holiday/setListParams',
    }),
    init() {
      if (this.listParams) {
        _.merge(this.filters, this.listParams.filters)
        _.merge(this.options, this.listParams.options)
      }
    },
    getListData() {
      const listParams = new ListParams(this.filters, this.options)
      this.getItemList(utils.toServerSideQuery(listParams))
      this.setListParams(listParams)
    },
    showDeleteDialog(item) {
      this.deleteDialog.item = item
      this.deleteDialog.show = true
    },
    searchClassroomList() {
      this.getClassroomList({ search: this.searchInputClassroom })
    },
  },
}
</script>
